import { useState, useMemo } from 'react';
import { Search, X } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EstadoBadge } from '../shared/EstadoBadge';
import { DetalleDistribucionModal } from './DetalleDistribucionModal';
import { formatCurrency } from '@/contabilidad/utils/formatters';
import { FONDO_LABELS } from '@/contabilidad/constants';
import type { DistribucionUtilidad } from '@/contabilidad/types';

interface HistorialDistribucionesProps {
  historial: DistribucionUtilidad[];
  onCancelar: (id: number) => Promise<unknown>;
  isCancelando?: boolean;
}

export function HistorialDistribuciones({ historial, onCancelar, isCancelando = false }: HistorialDistribucionesProps) {
  const [filtroPeriodo, setFiltroPeriodo] = useState('Todos');
  const [filtroEstado, setFiltroEstado] = useState('Todos');
  const [busqueda, setBusqueda] = useState('');
  const [seleccionada, setSeleccionada] = useState<DistribucionUtilidad | null>(null);

  const periodosUnicos = useMemo(() => {
    const set = new Set(historial.map(h => h.periodo));
    return Array.from(set);
  }, [historial]);

  const estadosUnicos = useMemo(() => {
    const set = new Set(historial.map(h => h.estado));
    return Array.from(set);
  }, [historial]);

  const historialFiltrado = useMemo(() => {
    return historial.filter(h => {
      const matchPeriodo  = filtroPeriodo === 'Todos' || h.periodo === filtroPeriodo;
      const matchEstado   = filtroEstado  === 'Todos' || h.estado  === filtroEstado;
      const matchBusqueda = busqueda === '' || h.periodo.toLowerCase().includes(busqueda.toLowerCase());
      return matchPeriodo && matchEstado && matchBusqueda;
    });
  }, [historial, filtroPeriodo, filtroEstado, busqueda]);

  function limpiarFiltros() {
    setFiltroPeriodo('Todos');
    setFiltroEstado('Todos');
    setBusqueda('');
  }

  const tieneFiltros = filtroPeriodo !== 'Todos' || filtroEstado !== 'Todos' || busqueda !== '';

  return (
    <div>
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">
        Historial de Distribuciones
      </p>

      {/* Filtros */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 items-end">
        {/* Período */}
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Período</label>
          <select
            value={filtroPeriodo}
            onChange={e => setFiltroPeriodo(e.target.value)}
            className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="Todos">Todos</option>
            {periodosUnicos.map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>

        {/* Estado */}
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Estado</label>
          <select
            value={filtroEstado}
            onChange={e => setFiltroEstado(e.target.value)}
            className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="Todos">Todos</option>
            {estadosUnicos.map(e => (
              <option key={e} value={e}>{e}</option>
            ))}
          </select>
        </div>

        {/* Buscar + Limpiar */}
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Buscar</label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
              <input
                type="text"
                value={busqueda}
                onChange={e => setBusqueda(e.target.value)}
                placeholder="Buscar por período..."
                className="w-full h-9 pl-8 pr-3 rounded-md border border-input bg-background text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
            {tieneFiltros && (
              <button
                onClick={limpiarFiltros}
                className="h-9 px-3 rounded-md border border-input bg-background text-xs text-muted-foreground hover:bg-accent flex items-center gap-1"
              >
                <X className="size-3" /> Limpiar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs font-semibold uppercase tracking-wider">Período</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider">Fondo</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider">Fecha Reparto</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-right">Monto ARS</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-right">Monto USD</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider">Estado</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {historialFiltrado.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">
                  {tieneFiltros ? 'No hay distribuciones que coincidan con los filtros.' : 'Aún no hay distribuciones registradas.'}
                </TableCell>
              </TableRow>
            ) : (
              historialFiltrado.map(h => (
                <TableRow
                  key={h.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setSeleccionada(h)}
                >
                  <TableCell className="font-semibold text-sm">{h.periodo}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{FONDO_LABELS[h.fondo]}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{h.fechaReparto}</TableCell>
                  <TableCell className="text-right text-sm tabular-nums font-medium">
                    {formatCurrency(h.montoARS, 'ARS')}
                  </TableCell>
                  <TableCell className="text-right text-sm tabular-nums font-medium text-muted-foreground">
                    {formatCurrency(h.montoUSD, 'USD')}
                  </TableCell>
                  <TableCell><EstadoBadge estado={h.estado} /></TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <DetalleDistribucionModal
        distribucion={seleccionada}
        onClose={() => setSeleccionada(null)}
        onCancelar={onCancelar}
        isCancelando={isCancelando}
      />
    </div>
  );
}
